import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

// Native scrolling keeps vertical page gestures, momentum and keyboard access intact.
export function useTouchRail(key: string, count: number) {
  const rail = useRef<HTMLDivElement>(null);
  const destination = useRef<{ index: number; left: number } | null>(null);
  const [active, setActive] = useState(0);
  useEffect(() => {
    const node = rail.current;
    if (!node) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const bounds = node.getBoundingClientRect();
      const center = bounds.left + bounds.width / 2;
      let closest = 0, distance = Infinity;
      Array.from(node.children).forEach((item, index) => {
        const card = item as HTMLElement, box = card.getBoundingClientRect();
        const delta = Math.abs(box.left + box.width / 2 - center);
        if (delta < distance) { closest = index; distance = delta; }
        card.style.setProperty('--rail-distance', String(Math.min(delta / bounds.width, 1)));
        card.style.setProperty('--rail-direction', String(Math.max(-1, Math.min(1, (box.left + box.width / 2 - center) / bounds.width))));
      });
      if (destination.current && Math.abs(node.scrollLeft - destination.current.left) < 2) destination.current = null;
      setActive(destination.current?.index ?? closest);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    destination.current = null;
    node.scrollLeft = 0;
    const interrupt = () => { destination.current = null; onScroll(); };
    node.addEventListener('scroll', onScroll, { passive: true });
    node.addEventListener('pointerdown', interrupt, { passive: true });
    node.addEventListener('wheel', interrupt, { passive: true });
    const resize = new ResizeObserver(() => { destination.current = null; update(); }); resize.observe(node);
    update();
    return () => { node.removeEventListener('scroll', onScroll); node.removeEventListener('pointerdown', interrupt); node.removeEventListener('wheel', interrupt); resize.disconnect(); cancelAnimationFrame(frame); };
  }, [key, count]);
  const move = (index: number) => {
    const node = rail.current, item = node?.children[index] as HTMLElement | undefined;
    if (!node || !item) return;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.dataset.motion === 'paused';
    const x = item.getBoundingClientRect().left - node.getBoundingClientRect().left + node.scrollLeft - (node.clientWidth - item.offsetWidth) / 2;
    const left = Math.max(0, Math.min(x, node.scrollWidth - node.clientWidth));
    destination.current = { index, left };
    setActive(index);
    node.scrollTo({ left, behavior: reduced ? 'auto' : 'smooth' });
  };
  return { rail, active, move };
}

export function RailControls({ active, count, move, label }: { active: number; count: number; move: (i: number) => void; label: string }) {
  if (!count) return null;
  return <div className="touch-rail-controls" role="group" aria-label={`${label} controls`}>
    <button aria-label={`Previous ${label}`} disabled={active === 0} onClick={() => move(active - 1)}><ArrowLeft size={19}/></button>
    <div><span className="rail-position" aria-live="polite" aria-atomic="true">{String(active + 1).padStart(2, '0')} <i>/ {String(count).padStart(2, '0')}</i></span><span className="rail-track" aria-hidden="true"><span style={{ width: `${100 / count}%`, transform: `translateX(${active * 100}%)` }}/></span><small>Swipe to explore</small></div>
    <button aria-label={`Next ${label}`} disabled={active >= count - 1} onClick={() => move(active + 1)}><ArrowRight size={19}/></button>
  </div>;
}
